import { useState, type FormEvent } from "react";
import { db } from "../lib/supabase";
import { useI18n } from "../i18n";
import { LangSwitch } from "../components/LangSwitch";

export function SignIn() {
  const { t } = useI18n();
  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      if (mode === "signin") {
        const { error } = await db().auth.signInWithPassword({ email, password });
        if (error) throw error;
      } else {
        const { data, error } = await db().auth.signUp({
          email,
          password,
          options: { data: { full_name: fullName.trim() }, emailRedirectTo: window.location.origin },
        });
        if (error) throw error;
        if (!data.session) {
          setNotice(t("checkEmail"));
          setMode("signin");
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t("errorGeneric"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="auth">
      <div className="auth-top">
        <LangSwitch />
      </div>
      <form className="auth-card" onSubmit={submit} noValidate>
        <img src="/peoplecore-logo.svg" alt="PeopleCore" height={40} className="auth-logo" />
        <h1>{mode === "signin" ? t("signInTitle") : t("signUpTitle")}</h1>
        {mode === "signup" && (
          <label className="field">
            <span>{t("fullName")}</span>
            <input id="full-name" value={fullName} onChange={(e) => setFullName(e.target.value)} autoComplete="name" required />
          </label>
        )}
        <label className="field">
          <span>{t("email")}</span>
          <input id="email" type="email" dir="ltr" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" required />
        </label>
        <label className="field">
          <span>{t("password")}</span>
          <input
            id="password"
            type="password"
            dir="ltr"
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete={mode === "signin" ? "current-password" : "new-password"}
            required
          />
          {mode === "signup" && <small>{t("passwordHint")}</small>}
        </label>
        {error && <p className="alert danger" role="alert">{error}</p>}
        {notice && <p className="alert info">{notice}</p>}
        <button className="btn primary block" disabled={busy || !email || password.length < 8}>
          {mode === "signin" ? t("signIn") : t("signUp")}
        </button>
        <p className="auth-switch">
          {mode === "signin" ? t("noAccount") : t("haveAccount")}{" "}
          <button type="button" className="link" onClick={() => setMode(mode === "signin" ? "signup" : "signin")}>
            {mode === "signin" ? t("switchToSignUp") : t("switchToSignIn")}
          </button>
        </p>
      </form>
    </main>
  );
}
